import React, { useState, useEffect } from "react";
import { StaffSideMenu } from "../staff/StaffSideMenu";
import { useParams, useNavigate } from "react-router-dom"; // useNavigate hook for going back
import API_URL from "../../api/API";
import { useStaffAuthContext } from "../staff/useStaffAuthContext";

export const AdminStaticWarmupRoutine = () => {
  const { staff } = useStaffAuthContext();
  const navigate = useNavigate(); // useNavigate hook

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [price, setPrice] = useState(0);

  const [phases, setPhases] = useState([]);
  const [exercises, setExercises] = useState([]);
  const [selected, setSelected] = useState({
    phases: {},
    exercises: {},
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => {
    const fetchAllData = async () => {
      try {
        setIsLoading(true);
        setError("");
      
        const [phasesResponse, exercisesResponse] = await Promise.all([
          fetch(API_URL.GET_PHASE_ALL_DATA, {
            method: "GET",
            headers: {
              Authorization: `Bearer ${staff.token}`,
            },
          }),
          fetch(API_URL.GET_PHASE_EXERCISE_ALL_DATA, {
            method: "GET",
            headers: {
              Authorization: `Bearer ${staff.token}`,
            },
          }),
        ]);

        if (!phasesResponse.ok || !exercisesResponse.ok) {
          setError("Failed to fetch phases or exercises data");
        }

        const phasesData = await phasesResponse.json();
        const exercisesData = await exercisesResponse.json();

        setPhases(phasesData.phases);
        setExercises(exercisesData.exercises);
       
      } catch (err) {
        console.error(err);
        setError(err.message || "Error fetching data");
      } finally {
        setIsLoading(false);
      }
    };

    fetchAllData();
  }, [staff.token]);

  const handleSave = async () => {
    setError("");
    setSuccess("");

    if (!title.trim()) {
      setError("Title is required.");
      return;
    }

    const selectedExercises = exercises
      .filter((ex) => selected.exercises[ex._id])
      .map((ex) => ({
        exercise_id: ex._id,
        phase_id: ex.phase_id,
        title: ex.title,
      }));

    const data = {
      title,
      description,
      price,
      selectedExercises,
    };

    try {
      const response = await fetch(API_URL.POST_STATIC_WARMUP_ROUTINE, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${staff.token}`,
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) setError("Failed to save data");

      if (response.ok) {
        setSuccess("Static Warmup routine saved successfully!");
        // Clear fields after success
        setTitle("");
        setDescription("");
        setPrice("");
        setSelected({ phases: {}, exercises: {} });
      }
    } catch (err) {
      setError(err.message || "Error saving data");
    }
  };

  const togglePhase = (phaseId) => {
    const isSelected = !selected.phases[phaseId];
    const updatedExercises = { ...selected.exercises };

    exercises
      .filter((ex) => ex.phase_id === phaseId)
      .forEach((ex) => {
        updatedExercises[ex._id] = isSelected;
      });

    setSelected((prev) => ({
      phases: { ...prev.phases, [phaseId]: isSelected },
      exercises: updatedExercises,
    }));
  };

  const toggleExercise = (exerciseId, phaseId) => {
    const isSelected = !selected.exercises[exerciseId];
    const exercisesInPhase = exercises.filter((ex) => ex.phase_id === phaseId);
    const allChecked = exercisesInPhase.every(
      (ex) =>
        ex._id === exerciseId ? isSelected : selected.exercises[ex._id]
    );

    setSelected((prev) => ({
      phases: { ...prev.phases, [phaseId]: allChecked },
      exercises: { ...prev.exercises, [exerciseId]: isSelected },
    }));
  };

  return (
    <div className="flex h-screen">
      <StaffSideMenu />

      <div className="flex-1 p-6 bg-gray-100 pt-16">
        <div className="absolute top-4 right-4">
          <button
            onClick={() => navigate(-1)} // Go back to the previous page
            className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
          >
            Go Back
          </button>
        </div>

        {error && (
          <div className="bg-red-100 text-red-500 p-3 rounded mb-4">
            {error}
          </div>
        )}

        {success && (
          <div className="bg-green-100 text-green-500 p-3 rounded mb-4">
            {success}
          </div>
        )}

        <h1 className="text-2xl font-bold mb-4">Static Warmup Routine</h1>

        <div className="bg-white p-4 rounded shadow">
          <div className="mb-4">
            <label className="block font-bold mb-2">Title</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full p-2 border rounded"
              placeholder="Enter title"
            />
          </div>

          <div className="mb-4">
            <label className="block font-bold mb-2">Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full p-2 border rounded"
              placeholder="Enter description"
            ></textarea>
          </div>

          {phases.map((phase) => (
            <div key={phase._id} className="mb-4">
              <div className="flex items-center mb-2">
                <input
                  type="checkbox"
                  checked={!!selected.phases[phase._id]}
                  onChange={() => togglePhase(phase._id)}
                  className="mr-2"
                />
                <span className="font-bold">{phase.title}</span>
              </div>

              <div className="pl-6">
                {exercises
                  .filter((ex) => ex.phase_id === phase._id)
                  .map((ex) => (
                    <div key={ex._id} className="flex items-center mb-1">
                      <input
                        type="checkbox"
                        checked={!!selected.exercises[ex._id]}
                        onChange={() => toggleExercise(ex._id, phase._id)}
                        className="mr-2"
                      />
                      <span>{ex.title}</span>
                    </div>
                  ))}
              </div>
            </div>
          ))}

          <button
            onClick={handleSave}
            className="px-4 py-2 mt-4 bg-blue-500 text-white rounded hover:bg-blue-600"
          >
            Create
          </button>
        </div>
      </div>
    </div>
  );
};
